import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Divider,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import bannerImage from "../../assets/banner.jpeg";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { showAppMessageToast } from "../../utils/toastUtils";
import { useDispatch } from "react-redux";
import { requestOtp } from "../../features/auth/authSlice";

// This file is a self-contained React application that recreates the Login with OTP page
// using Material-UI components, following the same responsive layout and design
// principles as the other auth pages. All external dependencies are mocked to
// make this a runnable, single-file example.

// MOCKING EXTERNAL DEPENDENCIES FOR A STANDALONE EXAMPLE
// const useNavigate = () => (path) => console.log(`Navigating to: ${path}`);
// const showAppMessageToast = ({ type, title, message }) => console.log(`${type}: ${title}\n${message}`);
// const bannerImage = "https://placehold.co/1080x1920/1A237E/fff?text=Login+with+OTP+Banner";

// Custom theme to match the colors from the login and register pages
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#ff9800",
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          // New style to target placeholder text on focus
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27", // Use a dark yellow for better contrast
            opacity: 1, // Ensure it is fully visible
          },
          // New style to target the label on focus
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
  },
});

// A custom Formik-compatible TextField component
const FormikTextField = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  const errorText = meta.error && meta.touched ? meta.error : "";
  return (
    <TextField
      {...props}
      {...field}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      error={!!errorText}
      helperText={errorText}
    />
  );
};

// Reusable Formik Select Field
const FormikSelectField = ({ label, options, ...props }) => {
  const [field, meta] = useField(props);
  const errorText = meta.touched && meta.error ? meta.error : "";
  return (
    <FormControl fullWidth margin="normal" error={!!errorText}>
      <InputLabel>{label}</InputLabel>
      <Select {...field} label={label}>
        <MenuItem value="">
          <em>Select a role</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

const LoginWithOtpSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  selected_role: Yup.string().required("Please select a role"),
});

const LoginWithOtp = () => {
  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The handleSubmit function now uses a mock API call for a standalone example.
  const handleRequestOtp = async (values, { setSubmitting }) => {
    try {
      const payload = {
        email: values.email,
        selected_role: values.selected_role, // ✅ include in body
      };

      // Simulate an API call
      const res = await dispatch(requestOtp(payload)).unwrap();

      showAppMessageToast({
        type: "success",
        title: "OTP Sent",
        message: "A one-time password has been sent to your email.",
      });
      navigate("/request-otp", {
        state: {
          email: values.email,
          role: values.selected_role,
          source: "login",
        },
      });
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Failed to Send OTP",
        message:
          typeof err === "string"
            ? err
            : err?.message || "Unable to send OTP. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - hidden on mobile */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${bannerImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />
          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              sx={{ my: 1, fontSize: "2.5rem" }}
            >
              Scrap Trade
            </Typography>
            <Typography variant="body1" sx={{ color: "#ddd" }}>
              Sign in securely with a one-time password.
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            Login with OTP
          </Typography>
          <Formik
            initialValues={{ email: "", selected_role: "" }}
            validationSchema={LoginWithOtpSchema}
            onSubmit={handleRequestOtp}
          >
            {({ isSubmitting }) => (
              <Form noValidate>
                <FormikTextField
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                />

                {/* New Role Dropdown */}
                <FormikSelectField
                  name="selected_role"
                  label="Role"
                  options={[
                    { label: "Buyer", value: "buyer" },
                    { label: "Seller", value: "seller" },
                  ]}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    backgroundColor: customTheme.palette.scraptradeYellow.main,
                    color: "#2D3A27",
                    "&:hover": {
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.dark,
                    },
                  }}
                  disabled={isSubmitting}
                >
                  Request OTP
                </Button>

                <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleBackToLogin}
                  sx={{
                    color: customTheme.palette.scraptradeYellow.main,
                    borderColor: customTheme.palette.scraptradeYellow.main,
                    "&:hover": {
                      borderColor: customTheme.palette.scraptradeYellow.dark,
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.main + "1a",
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <LoginWithOtp />
    </ThemeProvider>
  );
}
